/**
 * Shared background removal utility for photo placeholders.
 * Uses MediaPipe Image Segmenter with fallback to edge-detection for transparent output.
 */

interface MediaPipeSegmentationResult {
  categoryMask: {
    getAsUint8Array: () => Uint8Array
    width: number
    height: number
  }
}

let imageSegmenter: unknown = null
let segmenterInitPromise: Promise<unknown> | null = null

export const initializeImageSegmenter = async (): Promise<unknown> => {
  if (imageSegmenter) return imageSegmenter
  if (segmenterInitPromise) return segmenterInitPromise

  segmenterInitPromise = (async (): Promise<unknown> => {
    try {
      const { ImageSegmenter, FilesetResolver } = await import('@mediapipe/tasks-vision')

      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      )

      imageSegmenter = await ImageSegmenter.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath:
            'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/latest/selfie_multiclass_256x256.tflite',
          delegate: 'GPU'
        },
        runningMode: 'IMAGE',
        outputCategoryMask: true,
        outputConfidenceMasks: false
      })

      return imageSegmenter
    } catch (error) {
      console.error('Failed to initialize MediaPipe Image Segmenter:', error)
      segmenterInitPromise = null
      return null
    }
  })()

  return segmenterInitPromise
}

/**
 * Fallback: simple edge detection, sets background pixels to transparent (alpha = 0).
 */
function simpleBackgroundRemovalToTransparent(imageData: ImageData): ImageData {
  const data = imageData.data
  const width = imageData.width
  const height = imageData.height

  const edges = new Uint8Array(width * height)

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const tl =
        data[((y - 1) * width + (x - 1)) * 4] * 0.299 +
        data[((y - 1) * width + (x - 1)) * 4 + 1] * 0.587 +
        data[((y - 1) * width + (x - 1)) * 4 + 2] * 0.114
      const tm =
        data[((y - 1) * width + x) * 4] * 0.299 +
        data[((y - 1) * width + x) * 4 + 1] * 0.587 +
        data[((y - 1) * width + x) * 4 + 2] * 0.114
      const tr =
        data[((y - 1) * width + (x + 1)) * 4] * 0.299 +
        data[((y - 1) * width + (x + 1)) * 4 + 1] * 0.587 +
        data[((y - 1) * width + (x + 1)) * 4 + 2] * 0.114
      const ml =
        data[(y * width + (x - 1)) * 4] * 0.299 +
        data[(y * width + (x - 1)) * 4 + 1] * 0.587 +
        data[(y * width + (x - 1)) * 4 + 2] * 0.114
      const mr =
        data[(y * width + (x + 1)) * 4] * 0.299 +
        data[(y * width + (x + 1)) * 4 + 1] * 0.587 +
        data[(y * width + (x + 1)) * 4 + 2] * 0.114
      const bl =
        data[((y + 1) * width + (x - 1)) * 4] * 0.299 +
        data[((y + 1) * width + (x - 1)) * 4 + 1] * 0.587 +
        data[((y + 1) * width + (x - 1)) * 4 + 2] * 0.114
      const bm =
        data[((y + 1) * width + x) * 4] * 0.299 +
        data[((y + 1) * width + x) * 4 + 1] * 0.587 +
        data[((y + 1) * width + x) * 4 + 2] * 0.114
      const br =
        data[((y + 1) * width + (x + 1)) * 4] * 0.299 +
        data[((y + 1) * width + (x + 1)) * 4 + 1] * 0.587 +
        data[((y + 1) * width + (x + 1)) * 4 + 2] * 0.114

      const sobelX = tr + 2 * mr + br - (tl + 2 * ml + bl)
      const sobelY = bl + 2 * bm + br - (tl + 2 * tm + tr)
      const magnitude = Math.sqrt(sobelX * sobelX + sobelY * sobelY)

      edges[y * width + x] = magnitude > 80 ? 255 : 0
    }
  }

  const backgroundMask = new Uint8Array(width * height)
  const visited = new Array(width * height).fill(false)
  const stack: Array<{ x: number; y: number }> = []

  for (let x = 0; x < width; x++) {
    stack.push({ x, y: 0 })
    stack.push({ x, y: height - 1 })
  }
  for (let y = 0; y < height; y++) {
    stack.push({ x: 0, y })
    stack.push({ x: width - 1, y })
  }

  while (stack.length > 0) {
    const { x, y } = stack.pop()!
    const idx = y * width + x

    if (x < 0 || x >= width || y < 0 || y >= height || visited[idx]) continue

    const pixelIdx = idx * 4
    const r = data[pixelIdx]
    const g = data[pixelIdx + 1]
    const b = data[pixelIdx + 2]
    const brightness = r * 0.299 + g * 0.587 + b * 0.114

    const colorVariance = Math.sqrt(
      Math.pow(r - brightness, 2) + Math.pow(g - brightness, 2) + Math.pow(b - brightness, 2)
    )

    if (brightness > 160 && colorVariance < 30 && edges[idx] < 128) {
      visited[idx] = true
      backgroundMask[idx] = 255

      stack.push({ x: x + 1, y })
      stack.push({ x: x - 1, y })
      stack.push({ x, y: y + 1 })
      stack.push({ x, y: y - 1 })
    }
  }

  // Set background pixels to fully transparent
  for (let i = 0; i < data.length; i += 4) {
    const pixelIdx = Math.floor(i / 4)
    if (backgroundMask[pixelIdx] > 128) {
      data[i + 3] = 0
    }
  }

  return imageData
}

const bgRemovedCache = new Map<string, HTMLImageElement>()
const bgRemovalInFlight = new Map<string, Promise<HTMLImageElement>>()

/**
 * Remove background from image at URL and return an HTMLImageElement with transparent background.
 * Results are cached by URL. Concurrent calls for the same URL share a single processing promise.
 */
export async function removeBackground(imageUrl: string): Promise<HTMLImageElement> {
  const cached = bgRemovedCache.get(imageUrl)
  if (cached) return cached

  const inFlight = bgRemovalInFlight.get(imageUrl)
  if (inFlight) return inFlight

  const processingPromise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = async (): Promise<void> => {
      try {
        const devicePixelRatio = window.devicePixelRatio || 1

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          reject(new Error('Could not get canvas context'))
          return
        }

        canvas.width = img.width * devicePixelRatio
        canvas.height = img.height * devicePixelRatio
        ctx.scale(devicePixelRatio, devicePixelRatio)

        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, 0, 0, img.width, img.height)

        try {
          const segmenter = await initializeImageSegmenter()

          if (segmenter && typeof segmenter === 'object' && 'segment' in segmenter) {
            const segmentationResult = (
              segmenter as { segment: (img: HTMLImageElement) => unknown }
            ).segment(img)

            if (
              segmentationResult &&
              typeof segmentationResult === 'object' &&
              'categoryMask' in segmentationResult
            ) {
              const mask = (segmentationResult as MediaPipeSegmentationResult).categoryMask
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
              const data = imageData.data

              const maskData = mask.getAsUint8Array()
              const maskWidth = mask.width
              const maskHeight = mask.height

              for (let y = 0; y < canvas.height; y++) {
                for (let x = 0; x < canvas.width; x++) {
                  const canvasIdx = (y * canvas.width + x) * 4

                  const maskX = Math.floor((x / canvas.width) * maskWidth)
                  const maskY = Math.floor((y / canvas.height) * maskHeight)
                  const maskIdx = maskY * maskWidth + maskX

                  const category = maskData[maskIdx]

                  if (category === 0) {
                    data[canvasIdx + 3] = 0
                  }
                }
              }

              ctx.putImageData(imageData, 0, 0)
            } else {
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
              const processed = simpleBackgroundRemovalToTransparent(imageData)
              ctx.putImageData(processed, 0, 0)
            }
          } else {
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
            const processed = simpleBackgroundRemovalToTransparent(imageData)
            ctx.putImageData(processed, 0, 0)
          }
        } catch (mpError) {
          console.log('MediaPipe failed, using fallback method:', mpError)
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const processed = simpleBackgroundRemovalToTransparent(imageData)
          ctx.putImageData(processed, 0, 0)
        }

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'))
              return
            }
            const dataUrl = URL.createObjectURL(blob)
            const resultImg = new Image()
            resultImg.crossOrigin = 'anonymous'
            resultImg.onload = (): void => {
              bgRemovedCache.set(imageUrl, resultImg)
              resolve(resultImg)
            }
            resultImg.onerror = (): void => reject(new Error('Failed to load processed image'))
            resultImg.src = dataUrl
          },
          'image/png'
        )
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = (): void => {
      reject(new Error('Failed to load image'))
    }

    img.src = imageUrl
  })

  bgRemovalInFlight.set(imageUrl, processingPromise)

  return processingPromise.finally(() => {
    bgRemovalInFlight.delete(imageUrl)
  })
}

/**
 * Clear the background-removed image cache (e.g. when photos change).
 */
export function clearBackgroundRemovalCache(): void {
  bgRemovedCache.forEach((img) => {
    if (img.src && img.src.startsWith('blob:')) {
      URL.revokeObjectURL(img.src)
    }
  })
  bgRemovedCache.clear()
}
